import {
  Carousel,
  LazyCoverImage,
  NonBlockingSpinner,
  PageContainer,
  RoundedButton,
} from "@greeter/matter";
import { Card } from "@greeter/matter-v2";
import {
  TicketEventOverviewMachine,
  TicketEventOverviewMachineApiRequirements,
} from "./TicketEventOverviewMachine";
import {
  IonContent,
  IonFab,
  IonFabButton,
  IonFabList,
  IonIcon,
  IonPage,
  useIonViewDidEnter,
} from "@ionic/react";
import { classNames, isType } from "@greeter/util";
import { useActor, useSelector } from "@xstate/react";
import { motion } from "motion/react";
import { ImageAsset, VenueTicketEvent } from "@greeter/core";
import {
  IconBarcode,
  IconCalendar,
  IconCalendarDue,
  IconClipboardList,
  IconClock,
  IconUsers,
} from "@tabler/icons-react";
import { Routes } from "@greeter-venue/utility/Routes";
import { useFetchActorLoading } from "@greeter/xstate-util";
import { useHistory } from "react-router-dom";
import { addOutline, qrCodeOutline } from "ionicons/icons";

import css from "./TicketEventOverviewPage.module.scss";

const dateFormatter = new Intl.DateTimeFormat("da-DK", {
  year: "numeric",
  day: "numeric",
  month: "short",
});

function TicketEventCard(props: {
  ticketEvent: VenueTicketEvent;
  onEdit?: () => void;
  onOpenGuestList?: () => void;
}) {
  return (
    <motion.div
      style={{
        backgroundColor: "var(--gm-color-alt-bg)",
        borderRadius: "var(--gm-border-radius-md)",
        overflow: "hidden",
      }}
      onClick={props.onEdit}
    >
      <motion.div style={{ aspectRatio: 16 / 7 }}>
        <LazyCoverImage
          src={ImageAsset.findUriWithSizeOrDefault(
            props.ticketEvent.coverAssets[0],
            "16x9-w512"
          )}
        />
      </motion.div>
      <motion.div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          padding: 20,
        }}
      >
        <h1 style={{ textAlign: "left" }}>{props.ticketEvent.title}</h1>

        <TicketEventCard.Info>
          <TicketEventCard.InfoIcon icon="calendar" />
          <TicketEventCard.InfoText>
            {isType(props.ticketEvent.eventPeriod, "once") &&
              dateFormatter.format(
                new Date(props.ticketEvent.eventPeriod.date)
              )}
          </TicketEventCard.InfoText>
        </TicketEventCard.Info>

        <TicketEventCard.Info>
          <TicketEventCard.InfoIcon icon="clock" />
          <TicketEventCard.InfoText>
            {isType(props.ticketEvent.eventPeriod, "once") &&
              `kl. ${props.ticketEvent.eventPeriod.timePeriod.from.toString()} - ${props.ticketEvent.eventPeriod.timePeriod.to.toString()}`}
          </TicketEventCard.InfoText>
        </TicketEventCard.Info>

        <TicketEventCard.Info>
          <TicketEventCard.InfoIcon icon="people" />
          <TicketEventCard.InfoText>
            {props.ticketEvent.ticketCount ?? 0} billetter solgt
          </TicketEventCard.InfoText>
        </TicketEventCard.Info>

        <TicketEventCard.Info>
          <TicketEventCard.InfoIcon icon="scan" />
          <TicketEventCard.InfoText>
            {props.ticketEvent.ticketCheckedInCount ?? 0} scannet
          </TicketEventCard.InfoText>
        </TicketEventCard.Info>

        <TicketEventCard.Info>
          <TicketEventCard.InfoIcon icon="calendar-due" />
          <TicketEventCard.InfoText>
            {props.ticketEvent.salesPeriod
              ? `Kan købe mellem d. ${dateFormatter.format(
                  props.ticketEvent.salesPeriod.from
                )} - ${dateFormatter.format(props.ticketEvent.salesPeriod.to)}`
              : "Ingen salgsdeadline"}
          </TicketEventCard.InfoText>
        </TicketEventCard.Info>

        <motion.div style={{ display: "flex", justifyContent: "start" }}>
          <Card act="button" variant="green" style={{ width: "fit-content" }}>
            <Card.Content
              padding="sm"
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 5,
                alignItems: "center",
              }}
              onClick={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                props.onOpenGuestList?.();
              }}
            >
              <IconClipboardList />
              Se gæsteliste
            </Card.Content>
          </Card>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

TicketEventCard.Info = function ({
  style,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        gap: 5,
        alignItems: "center",
        ...style,
      }}
      {...props}
    />
  );
};
TicketEventCard.InfoText = function ({
  style,
  ...props
}: React.ComponentProps<"span">) {
  return <span style={{ textAlign: "left" }} {...props} />;
};
TicketEventCard.InfoIcon = function (props: {
  icon: "people" | "clock" | "calendar" | "calendar-due" | "scan";
}) {
  return props.icon === "people" ? (
    <IconUsers />
  ) : props.icon === "clock" ? (
    <IconClock />
  ) : props.icon === "calendar-due" ? (
    <IconCalendarDue />
  ) : props.icon === "scan" ? (
    <IconBarcode />
  ) : (
    <IconCalendar />
  );
};

export type TicketEventOverviewPageProps = {
  api: TicketEventOverviewMachineApiRequirements;
};

export function TicketEventOverviewPage(props: TicketEventOverviewPageProps) {
  const [snap, send, m] = useActor(TicketEventOverviewMachine, {
    input: { api: props.api },
  });

  const router = useHistory();

  const ticketEvents = useSelector(snap.context.api.fetchTicketEventsRef, (s) =>
    s.context.data?.toSorted((a, b) =>
      a.eventPeriod.type === "once" && b.eventPeriod.type === "once"
        ? b.eventPeriod.date < a.eventPeriod.date
          ? 1
          : -1
        : 0
    )
  );
  const fetchTicketEventsLoading = useFetchActorLoading(
    snap.context.api.fetchTicketEventsRef
  );

  const filter = useSelector(m, (s) => s.context.filter);

  useIonViewDidEnter(() => send({ type: "fetchTicketEvents" }), [send]);

  return (
    <IonPage className={css.Page}>
      <Carousel style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <RoundedButton
          key="all previous"
          className={css.FilterButton}
          activeClassName={css.Active}
          active={filter === "previous"}
          onClick={() => send({ type: "set.filter", data: "previous" })}
        >
          Tidligere
        </RoundedButton>
        <RoundedButton
          key="future"
          className={css.FilterButton}
          activeClassName={css.Active}
          active={filter === "future"}
          onClick={() => send({ type: "set.filter", data: "future" })}
        >
          Fremtidige
        </RoundedButton>
      </Carousel>
      <IonContent {...classNames(css.ContentContainer, css.InfiniteScroll)}>
        <PageContainer style={{ padding: 20 }}>
          {ticketEvents &&
            ticketEvents.map((te) => {
              return (
                <TicketEventCard
                  ticketEvent={te}
                  onEdit={() =>
                    router.push(
                      Routes.mainGroup.editTicketEvent.route({ id: te.id })
                    )
                  }
                  onOpenGuestList={() => {
                    sessionStorage.setItem("ticketEventId", te.id);
                    router.push(Routes.ticketsGroup.ticketsTab.root.route());
                  }}
                />
              );
            })}
        </PageContainer>
        <IonFab horizontal="end" vertical="bottom" slot="fixed">
          <IonFabButton>
            <IonIcon icon={addOutline} />
          </IonFabButton>
          <IonFabList side="top">
            <IonFabButton
              id="fab-create-booking"
              onClick={() =>
                router.push(Routes.mainGroup.editTicketEvent.route({ id: "" }))
              }
            >
              <IonIcon icon={addOutline} />
            </IonFabButton>
            <IonFabButton
              onClick={() =>
                router.push(Routes.ticketsGroup.scannerTab.root.route())
              }
            >
              <IonIcon icon={qrCodeOutline} />
            </IonFabButton>
          </IonFabList>
        </IonFab>
      </IonContent>
      <NonBlockingSpinner
        reason={fetchTicketEventsLoading && "Henter events"}
      />
    </IonPage>
  );
}
